import { useState, useRef } from 'react';
import toast from 'react-hot-toast';
import { resizePhoto, ROLE_LIST } from '../utils/people';

/* Shared profile pieces: avatar, role chips, the profile card and the editor.
   The editor is used by admins (all fields) and by each person for their own profile
   (photo, bio and fun fact only — `selfMode`).                                          */

export const ROLE_STYLE = {
  'Sales Rep':    { bg: '#E6F1FB', fg: '#0C447C', icon: '💼' },
  'Telemarketer': { bg: '#EEEDFE', fg: '#3C3489', icon: '📞' },
  'Canvasser':    { bg: '#E1F5EE', fg: '#0F6E56', icon: '🚶' },
  'Confirmer':    { bg: '#FBEAF0', fg: '#72243E', icon: '🎧' },
  'Production':   { bg: '#FFF3E0', fg: '#8A4B00', icon: '🛠️' },
};
const AV = ['#2d6e35', '#1976d2', '#8e24aa', '#d4a017', '#e0533d', '#0e9488', '#7c3aed', '#c2410c'];
const avColor = name => { let h = 0; for (const ch of (name || '')) h = (h * 31 + ch.charCodeAt(0)) % AV.length; return AV[h]; };
const initials = n => (n || '?').trim().split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

export function Avatar({ name, photo, size = 44 }) {
  const st = { width: size, height: size, borderRadius: '50%', flexShrink: 0, objectFit: 'cover', display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff', fontWeight: 800, fontSize: size * 0.36, background: `linear-gradient(135deg,${avColor(name)},${avColor(name)}cc)`, boxShadow: '0 2px 6px rgba(0,0,0,.15)' };
  return photo ? <img src={photo} alt={name} style={st} /> : <div style={st} aria-hidden>{initials(name)}</div>;
}
export function RoleChips({ roles, main }) {
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
      {roles.map(r => { const s = ROLE_STYLE[r] || { bg: '#eee', fg: '#444', icon: '👤' }; return (
        <span key={r} style={{ background: s.bg, color: s.fg, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>
          {s.icon} {r}{main && r === main && roles.length > 1 ? ' · main' : ''}
        </span>); })}
    </span>
  );
}

const CSS = `
.pe{display:flex;flex-direction:column;gap:20px;font-family:'Inter',system-ui,sans-serif;color:#13211B}
.pe-top{display:flex;gap:18px;align-items:center;flex-wrap:wrap;padding-bottom:20px;border-bottom:1px solid #D9E2DC}
.pe-photo{display:flex;flex-direction:column;align-items:center;gap:10px}
.pe-photo button{font:inherit;font-size:13px;font-weight:700;border:1.5px solid #0D3B2E;background:#fff;border-radius:999px;padding:7px 14px;cursor:pointer;color:#0D3B2E}
.pe-photo button:hover{background:#EEF2EF}
.pe-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.pe label{display:block;font-size:14px;font-weight:650;color:#13211B;margin-bottom:6px}
.pe label .opt{font-weight:500;color:#5E6E66}
.pe input,.pe textarea,.pe select{width:100%;box-sizing:border-box;font:inherit;font-size:15px;padding:11px 13px;border:1.5px solid #D9E2DC;border-radius:12px;background:#fff;color:#13211B;transition:border-color .15s}
.pe input:focus,.pe textarea:focus,.pe select:focus{outline:none;border-color:#B8912E;box-shadow:0 0 0 3px rgba(184,145,46,.18)}
.pe textarea{resize:vertical;min-height:130px;line-height:1.6}
.pe .help{font-size:12.5px;color:#5E6E66;margin-top:6px;line-height:1.45}
.pe .count{float:right}
.pe .err{color:#A3322A;font-weight:600;background:#FBEDEB;border-radius:10px;padding:10px 12px}
.pe-roles{display:flex;flex-wrap:wrap;gap:8px}
.pe-role{display:flex;align-items:center;gap:7px;border:1.5px solid #D9E2DC;border-radius:999px;padding:8px 14px;cursor:pointer;font-size:14px;font-weight:600;user-select:none;background:#fff}
.pe-role.on{border-color:#0D3B2E;background:#0D3B2E;color:#fff!important}
.pe-role input{width:auto;margin:0;accent-color:#B8912E}
.pe-foot{display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;padding-top:6px}
.pe-btn{font:inherit;font-weight:700;font-size:15px;border-radius:999px;padding:12px 22px;cursor:pointer;border:1.5px solid #0D3B2E;background:#fff;color:#0D3B2E}
.pe-btn.main{background:#0D3B2E;color:#fff}
.pe-btn.main:hover{background:#1A5240}
.pe-btn:disabled{opacity:.55;cursor:default}
.pe-btn:focus-visible,.pe-photo button:focus-visible{outline:3px solid #B8912E;outline-offset:2px}
@media(max-width:560px){.pe-grid{grid-template-columns:1fr}}
`;

export function ProfileEditor({ person, selfMode = false, onSave, onPhoto, onCancel, saving }) {
  const [f, setF] = useState(() => ({
    name: person?.name || '', nickname: person?.nickname || '', roles: person?.roles?.length ? person.roles : ['Sales Rep'],
    role: person?.role || person?.roles?.[0] || 'Sales Rep', status: person?.status || 'Active', bio: person?.bio || '', fun_fact: person?.fun_fact || '',
  }));
  const [photo, setPhoto] = useState(person?.photo || null);
  const [newPhoto, setNewPhoto] = useState(null);     // dataURL waiting to upload (new person, or before save)
  const [err, setErr] = useState('');
  const fileRef = useRef();
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const toggleRole = r => setF(x => {
    const roles = x.roles.includes(r) ? x.roles.filter(y => y !== r) : [...x.roles, r];
    return { ...x, roles, role: roles.includes(x.role) ? x.role : (roles[0] || x.role) };
  });

  async function pickPhoto(e) {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    try {
      const data = await resizePhoto(file);
      setPhoto(data);
      if (onPhoto && person?.id) { await onPhoto(data); toast.success('Photo updated'); }
      else setNewPhoto(data);
    } catch (x) { toast.error(x.response?.data?.error || x.message || 'Could not use that photo'); }
  }
  async function save() {
    setErr('');
    if (!selfMode) {
      if (!f.name.trim()) return setErr('Name is required.');
      if (!f.roles.length) return setErr('Pick at least one role.');
    }
    try { await onSave(f, newPhoto); } catch (x) { setErr(x.response?.data?.error || 'Could not save.'); }
  }

  return (
    <div className="pe">
      <style>{CSS}</style>
      <div className="pe-top">
        <div className="pe-photo">
          <Avatar name={f.name} photo={photo} size={104} />
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={pickPhoto} />
          <button type="button" onClick={() => fileRef.current?.click()}>{photo ? 'Change photo' : 'Add photo'}</button>
        </div>
        <div style={{ flex: 1, minWidth: 200 }}>
          {selfMode ? (
            <>
              <div style={{ fontFamily: "'Fraunces',Georgia,serif", fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>{f.name}</div>
              {f.nickname && <div style={{ fontFamily: "'Fraunces',Georgia,serif", fontStyle: 'italic', fontSize: 17, fontWeight: 600, color: '#B8912E', marginTop: 2 }}>“{f.nickname}”</div>}
              <div className="help">A square, well-lit photo of your face works best. Your name, nickname and roles are set by your manager.</div>
            </>
          ) : (
            <div className="pe-grid">
              <div><label>Full name</label><input value={f.name} onChange={e => set('name', e.target.value)} placeholder="First and last name" />
                {person?.id && <div className="help">Renaming doesn't change the name on jobs already entered.</div>}</div>
              <div><label>Nickname <span className="opt">· must be unique</span></label><input value={f.nickname} maxLength={30} onChange={e => set('nickname', e.target.value)} placeholder="e.g. R-Money" />
                <div className="help">Shown next to their name everywhere. No two people can share one.</div></div>
            </div>
          )}
        </div>
      </div>

      {!selfMode && (
        <>
          <div>
            <label>Roles <span className="opt">· pick all that apply</span></label>
            <div className="pe-roles">
              {ROLE_LIST.map(r => (
                <label key={r} className={'pe-role' + (f.roles.includes(r) ? ' on' : '')} style={{ margin: 0 }}>
                  <input type="checkbox" checked={f.roles.includes(r)} onChange={() => toggleRole(r)} />{(ROLE_STYLE[r] || {}).icon} {r}
                </label>
              ))}
            </div>
            <div className="help">They show up in the record pickers and on the Leaderboard for every role ticked here.</div>
          </div>
          <div className="pe-grid">
            <div><label>Main role</label>
              <select value={f.role} onChange={e => set('role', e.target.value)}>{f.roles.map(r => <option key={r}>{r}</option>)}</select>
              <div className="help">Used where only one role fits (payroll and commission rates).</div></div>
            <div><label>Status</label>
              <select value={f.status} onChange={e => set('status', e.target.value)}><option>Active</option><option>Inactive</option></select>
              <div className="help">Inactive people are hidden from pickers and the Leaderboard; their jobs stay.</div></div>
          </div>
        </>
      )}

      <div>
        <label>{selfMode ? 'About me' : 'Bio'} <span className="opt">{selfMode ? '· what you do at CPR' : '· what they do at CPR'}</span></label>
        <textarea maxLength={600} value={f.bio} onChange={e => set('bio', e.target.value)}
          placeholder={selfMode ? 'e.g. I run exterior coating appointments in the Inland Empire. Two years with CPR.' : 'What they do at CPR'} />
        <div className="help">{selfMode ? 'Your trade, your area, how long you\u2019ve been with CPR.' : 'Shown on their profile.'} <span className="count">{f.bio.length}/600</span></div>
      </div>
      <div>
        <label>Fun fact <span className="opt">· optional</span></label>
        <input maxLength={200} value={f.fun_fact} onChange={e => set('fun_fact', e.target.value)} placeholder="e.g. I've run three marathons" />
      </div>

      {err && <div className="help err">{err}</div>}
      <div className="pe-foot">
        {onCancel && <button type="button" className="pe-btn" onClick={onCancel}>Cancel</button>}
        <button type="button" className="pe-btn main" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save profile'}</button>
      </div>
    </div>
  );
}
